import { IntegrationApiFeaturesCongestionReportsContractsDataUnderlyingAsset as UnderlyingAssets } from '@alliander-fe/api';

import { CongestionReportsParagraphViewProps } from '../_View';
import { FALLBACK_VALUE } from '../constant/constants';

import { assetDataMapper } from './assetDataMapper';
import { getUpdatedAtDate } from './getUpdatedAtDate';
import { getCauses } from './getCauses';
import { getCausesText } from './getCausesText';
import { getReplaceText } from './getReplaceText';
import { readableAssetType } from './readableAssetType';
import { researchApplicable } from './researchApplicable';
import { stringToRichTextList } from './stringToRichText';
import { getCongestionManagementText } from './getCongestionManagementText';
import { reachedMaxCapacity } from './reachedMaxCapacity';
import { reportDates } from './reportDates';

export type AssetType =
  | 'NoScarcity'
  | 'MainAssetOnly'
  | 'UnderlyingAssetsOnly'
  | 'BothMainAndUnderlyingAssets';

export const getAssetData = ({
  congestionReport,
  isLoading,
  isError,
  congestionManagementApplicable,
  congestionManagementNotApplicable,
  congestionManagementNotYetApplicable,
  supplyResearchCongestionManagementText,
  consumeResearchCongestionManagementText,
  availableCapacityHigherThanRequiredCapacityText: availableCapacityText,
  ...fields
}: CongestionReportsParagraphViewProps) => {
  const assetType: AssetType | undefined = congestionReport?.scarcityLevel
    ? (congestionReport?.scarcityLevel as AssetType)
    : undefined;

  if (!assetType) return null;

  const updatedAtDate = getUpdatedAtDate({
    announcementDate: congestionReport?.announcementDate,
    publicationDate: congestionReport?.congestionManagementPublicationDate,
    updatedAtDate: congestionReport?.updatedAtDate,
  });

  const congestionCauseText = getCausesText({
    cause: congestionReport?.underlyingAssets,
    causeCapacityText: fields.causeCapacityText,
    causeVoltageText: fields.causeVoltageText,
    causeGridSecurityText: fields.causeGridSecurityText,
  });

  const causes = getCauses(congestionReport?.underlyingAssets);
  const replaceText = getReplaceText({
    ...congestionReport,
    updatedAtDate: updatedAtDate,
  });

  const researchApplicableText = researchApplicable({
    congestionManagementApplicable: congestionManagementApplicable?.toString(),
    congestionManagementNotApplicable: congestionManagementNotApplicable?.toString(),
    congestionManagementNotYetApplicable: congestionManagementNotYetApplicable?.toString(),
    isResearchApplicable: congestionReport?.congestionManagementApplicable,
  });

  const maxCapacityReached = reachedMaxCapacity(
    congestionReport?.availableTransportCapacity,
    congestionReport?.requiredTransportCapacityInSolutionYear
  );

  const availableCapacityHigherThanRequiredCapacityText =
    typeof maxCapacityReached === 'boolean' && !maxCapacityReached && Boolean(availableCapacityText)
      ? availableCapacityText
      : undefined;

  const congestionManagementText = getCongestionManagementText({
    congestionType: congestionReport?.congestionType,
    supplyResearchCongestionManagementText: supplyResearchCongestionManagementText,
    consumeResearchCongestionManagementText: consumeResearchCongestionManagementText,
  });

  const reportDatesString = reportDates({
    publicationDate: congestionReport?.congestionManagementPublicationDate,
    updatedAtDate: congestionReport?.congestionManagementUpdatedAtDate,
    fallbackValue: FALLBACK_VALUE,
  });

  // @todo: Add correct data for the main asset
  const mainAsset: UnderlyingAssets | undefined =
    assetType === 'BothMainAndUnderlyingAssets'
      ? {
          name: `Station ${congestionReport?.name}`,
          cause: congestionReport?.causeMainAsset,
          expectedEndDate: congestionReport?.resolvedDateMainAsset,
        }
      : undefined;

  return assetDataMapper({
    assetType: assetType,
    fields: {
      ...fields,
      researchApplicableText,
      mainAsset,
      congestionManagementText,
      availableCapacityHigherThanRequiredCapacityText,
      reportDates: reportDatesString,
    },
    replaceText: {
      ...replaceText,
      '{congestionCauseText}': congestionCauseText ? congestionCauseText : 'een onbekende oorzaak.',
      '{assetType}': readableAssetType(assetType),
      '{causes}': stringToRichTextList(causes) ?? FALLBACK_VALUE,
    },
  });
};
